import * as Service from 'types/service';
import { concat, filter, flattenDeep, sortBy } from 'es-toolkit/compat';
import {
  findPrimaryStateInList,
  getDisplayInfoForPartyState,
  isPartyOriginallyWalkIn,
} from 'utils/partyUtils';
import PartyStates from 'constants/partyStateConstants';

export type PartyStateSelectorStatus =
  | 'EXPECTED_TABLE'
  | 'EXPECTED_NO_TABLE'
  | 'SEATED'
  | 'FINISHED_TABLE'
  | 'FINISHED_NO_TABLE'
  | 'NO_SHOW'
  | 'CANCELLED';

const getProperStateStyling = (
  stateDescriptions: Service.StateDescription[],
  primaryState: Service.PartyState,
  partyStatusRefreshEnabled: boolean
): Service.StateDescription => {
  return (
    findPrimaryStateInList(stateDescriptions, primaryState) ||
    getDisplayInfoForPartyState(primaryState, partyStatusRefreshEnabled)
  );
};

export const getPartyStateSelectorOptions = ({
  party,
  status,
  secondaryStates,
  partyStatusRefreshEnabled,
}: {
  party: Service.Party;
  status: PartyStateSelectorStatus;
  secondaryStates: Service.StateGroupingMap;
  partyStatusRefreshEnabled: boolean;
}): Service.StateDescription[] => {
  const seatedState = getProperStateStyling(
    secondaryStates.SEATED,
    PartyStates.seated,
    partyStatusRefreshEnabled
  );
  const expectedState = getProperStateStyling(
    secondaryStates.EXPECTED,
    PartyStates.expected,
    partyStatusRefreshEnabled
  );
  const noShowState = !isPartyOriginallyWalkIn(party) ? secondaryStates.NO_SHOW : []; // You can't no-show a walkin
  const partiallySeatedState = findPrimaryStateInList(
    secondaryStates.EXPECTED,
    PartyStates.partiallyseated
  );
  switch (status) {
    case 'EXPECTED_TABLE':
      return flattenDeep(
        concat(sortBy(secondaryStates.EXPECTED, 'ordering'), seatedState, noShowState)
      );
    case 'EXPECTED_NO_TABLE':
      return flattenDeep(
        concat(
          filter(
            sortBy(secondaryStates.EXPECTED, 'ordering'),
            (stateOption) => stateOption.primaryState !== PartyStates.partiallyseated
          ),
          noShowState
        )
      );
    case 'SEATED': {
      const allButNoShowAndLeft = concat(
        expectedState,
        partiallySeatedState || [],
        sortBy(secondaryStates.SEATED, 'ordering')
      );
      return flattenDeep(concat(allButNoShowAndLeft, secondaryStates.LEFT, noShowState));
    }
    case 'FINISHED_TABLE':
      return flattenDeep(concat([expectedState], [seatedState], secondaryStates.LEFT, noShowState));
    case 'FINISHED_NO_TABLE': {
      const expectedStates = concat(secondaryStates.EXPECTED, [expectedState]);
      return flattenDeep(
        concat(sortBy(expectedStates, 'ordering'), secondaryStates.LEFT, noShowState)
      );
    }
    case 'NO_SHOW':
      return flattenDeep(concat([expectedState], noShowState));
    case 'CANCELLED':
      return secondaryStates.CANCELED;
    default:
      return [];
  }
};
